import { useEffect, useState } from 'react'
import { Check, Loader2, Smartphone, Upload } from 'lucide-react'
import { api, ApiError, type UploadProgress } from '../api'
import { Button, Card, CardTitle, ErrorText } from '../components/ui'

type Session = { status: 'waiting' | 'uploading' | 'done' | 'failed'; category?: 'insert' | 'source' | 'environment'; expires_at: string; filename?: string }

const bytes = (value: number) => value >= 1024 * 1024
  ? `${(value / 1024 / 1024).toFixed(1)} MB`
  : `${Math.ceil(value / 1024)} KB`

export function PhoneUpload({ token }: { token: string }) {
  const [session, setSession] = useState<Session | null>(null)
  const [checking, setChecking] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState<UploadProgress | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<Session>(`/api/phone-uploads/${encodeURIComponent(token)}`)
      .then(setSession)
      .catch(err => setError(err instanceof ApiError ? err.message : 'This upload link is unavailable'))
      .finally(() => setChecking(false))
  }, [token])

  async function pick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setUploading(true); setError(''); setProgress(null)
    try {
      const done = await api.upload<Session>(
        `/api/phone-uploads/${encodeURIComponent(token)}`, file, setProgress)
      setSession(done)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload failed')
      setSession(current => current ? { ...current, status: 'failed' } : current)
    } finally {
      setUploading(false)
    }
  }

  const pct = progress?.fraction == null ? null : Math.round(progress.fraction * 100)
  const isVideo = session?.category === 'insert'
  return (
    <main className="grid min-h-screen place-items-center bg-surface-0 px-5 py-8">
      <Card className="w-full max-w-md space-y-5 border-brand/25">
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
            {session?.status === 'done' ? <Check /> : <Smartphone />}
          </span>
          <div>
            <CardTitle>{session?.status === 'done' ? 'File upload complete' : `Upload ${isVideo ? 'product demo' : 'photo'}`}</CardTitle>
            <p className="mt-1 text-sm text-ink-faint">
              {session?.status === 'done'
                ? `${session.filename ?? 'Your file'} is now available in the web app.`
                : `Choose ${isVideo ? 'a recording' : 'a photo'} from this phone. You can close this page after it finishes.`}
            </p>
          </div>
        </div>

        {checking && <div className="flex items-center gap-2 text-sm text-ink-dim"><Loader2 className="animate-spin" /> Checking link…</div>}
        <ErrorText error={error} />

        {!checking && !error && session?.status !== 'done' && session?.status !== 'failed' && (
          <label className="flex cursor-pointer flex-col items-center gap-3 rounded-[--radius-control] border border-dashed border-line px-5 py-8 text-center hover:border-line-strong hover:bg-surface-2/50">
            {uploading ? <Loader2 className="size-6 animate-spin text-brand" /> : <Upload className="size-6 text-brand" />}
            <span className="text-sm font-medium">{uploading ? 'Uploading…' : 'Choose video'}</span>
            {uploading && progress && (
              <span className="w-full space-y-2">
                <span className="block h-1.5 overflow-hidden rounded-full bg-surface-3">
                  <span className="block h-full bg-brand transition-[width]" style={{ width: `${pct ?? 100}%` }} />
                </span>
                <span className="block text-xs text-ink-faint">
                  {pct == null ? 'Finishing…' : `${pct}% · ${bytes(progress.loaded)} of ${bytes(progress.total)}`}
                </span>
              </span>
            )}
            <input type="file" accept={isVideo ? 'video/mp4,video/quicktime,.mp4,.mov' : 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp'} onChange={pick} disabled={uploading} className="sr-only" />
          </label>
        )}

        {session?.status === 'done' && <Button className="w-full" onClick={() => window.close()}>Close</Button>}
      </Card>
    </main>
  )
}
